import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { postData } from '../../helpers/apiUtils';
import type { HubSite } from './types';

/**
 * Vergleichsseite anlegen — der zweite Seitentyp des Hubs (viele Broker, kein eigener).
 *
 * Bewusst ohne Broker-Auswahl: welche Broker die Seite zeigt, entscheidet der Baustein im Editor,
 * und daraus wird die Broker-Zuordnung abgeleitet. Ein zweiter Ort, an dem dasselbe steht, wäre
 * genau die Doppelpflege, die dieser Umbau abschafft.
 */
interface Props {
  open: boolean;
  sites: HubSite[];
  /**
   * Die vorhandenen Vergleichsseiten als moegliche Vorlage.
   *
   * ⚠ Das sind LISTENdaten ohne `content_blocks` (`BrokerHubService.listComparePages`) — der
   * Dialog braucht den Inhalt auch nicht. Kopiert wird serverseitig ueber `source`; hier steht
   * nur, was zur Auswahl gehoert.
   */
  pages?: Array<{ id: number; site_key: string | null; page_slug: string; page_title: string | null }>;
  onClose: () => void;
  onCreated: (profile: any) => void;
}

const NewComparePageDialog: React.FC<Props> = ({
  open,
  sites,
  pages = [],
  onClose,
  onCreated,
}) => {
  const [siteKey, setSiteKey] = useState('');
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const zuruecksetzen = (): void => {
    setSiteKey('');
    setSlug('');
    setTitle('');
    setSource('');
    setError(null);
  };

  const vorlage = pages.find((p) => String(p.id) === source) ?? null;
  // Das Design haengt am Baustein, nicht am Portal — eine Kopie auf ein anderes Portal sieht
  // zunaechst aus wie die Quelle. Der Hinweis steht schon VOR dem Anlegen da, damit niemand es
  // erst an der fertigen Seite merkt. Die Antwort des Servers nennt zusaetzlich die konkreten
  // Varianten.
  const portalWechsel = Boolean(vorlage && siteKey && vorlage.site_key !== siteKey);

  const anlegen = async (): Promise<void> => {
    setError(null);
    if (!siteKey || !slug.trim()) {
      setError('Portal und Adresse sind Pflicht.');
      return;
    }
    setBusy(true);
    const res = await postData<any>('/api/broker-hub/pages', {
      site_key: siteKey,
      page_slug: slug.trim().toLowerCase(),
      page_title: title.trim() || undefined,
      source: source || undefined,
    });
    setBusy(false);
    if (!res.success || !res.data) {
      setError(res.error || 'Anlegen fehlgeschlagen');
      return;
    }
    zuruecksetzen();
    onCreated(res.data);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        zuruecksetzen();
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Neue Vergleichsseite</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            select
            size="small"
            label="Portal"
            value={siteKey}
            onChange={(e) => setSiteKey(e.target.value)}
          >
            {sites.map((s) => (
              <MenuItem key={s.site_key} value={s.site_key}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Adresse (Slug)"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="cfd-broker-vergleich"
            helperText="Kleinbuchstaben und Bindestriche. Die Seite entsteht unter /broker/<slug>/."
          />
          <TextField
            size="small"
            label="Seitentitel (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="CFD Broker Vergleich 2026"
            helperText="Leer lassen behält bei einer bestehenden Seite deren SEO-Titel."
          />
          {/* Wortlaut und Aufbau wie im Profil-Dialog („Start-Inhalt"), damit beide Anlegewege
              dieselbe Bedienung haben. Kopiert wird serverseitig über `source`. */}
          <TextField
            select
            size="small"
            label="Start-Inhalt"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            helperText="Eine Kopie übernimmt Bausteine, Texte, CSS und Seiten-JavaScript. Broker-Daten (Logo, Note, Link, Risikohinweis) kommen beim Ausspielen aus dem Zielportal."
          >
            <MenuItem value="">Leer beginnen</MenuItem>
            {pages.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>
                Kopie von {p.page_title || p.page_slug} ({(p.site_key ?? '?').toUpperCase()})
              </MenuItem>
            ))}
          </TextField>
          {portalWechsel && (
            <Alert severity="info">
              Die Kopie geht auf ein anderes Portal. Das Design der Vorlage bleibt unverändert — es
              hängt am Baustein, nicht am Portal. Links und Bilder können weiter auf{' '}
              {(vorlage?.site_key ?? '').toUpperCase()} zeigen; bitte in der Vorschau prüfen.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            Die Seite wird als Entwurf angelegt und im Blocks-Editor befüllt. Welche Broker sie
            zeigt, bestimmen die Bausteine „Broker-Rangliste“ und „Broker-Box“ — Note, Logo,
            Affiliate-Link und Risikohinweis kommen dabei aus den Broker-Daten.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            zuruecksetzen();
            onClose();
          }}
        >
          Abbrechen
        </Button>
        <Button variant="contained" onClick={anlegen} disabled={busy}>
          Anlegen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewComparePageDialog;
